/**
 * Gold, the one resource (owner decision 2026-09-30; DESIGN §9.3): wood, iron and crystal merged into a single amount.
 *
 * The conversion is by value, so progression pacing is unchanged. Chests, Orders, Tribute, check-in, events and every
 * "supplies" item paid one rolled total split 50/35/15 into wood/iron/crystal; that total is now paid whole as gold
 * (1 gold = 1 unit of the old total). Upgrades consumed the three at 30:20:8 (towers/base) and 15:10:3 (gear +levels),
 * so wood ran out first at every level (30 / 0.50 = 60 chest-units against 20 / 0.35 = 57 and 8 / 0.15 = 53): a cost
 * in gold is the chest total its wood needed, wood ÷ 0.5 (tower base 60, gear base 30), and an upgrade costs the same
 * number of average chests as before. Fixed amounts in wood/iron/crystal (the Starter chest, salvage) convert the same
 * way: their wood's worth.
 */

/** Gold per unit of legacy wood: 1 ÷ the old chest split's wood share (0.5), wood having been the binding resource. */
export const LEGACY_WOOD_GOLD = 2;

/** A pre-gold resource bundle as older saves hold it. */
export interface LegacyResources { wood?: unknown; iron?: unknown; crystal?: unknown }

const count = (v: unknown): number => (typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0);

/**
 * Gold for a legacy bundle (save migration): its wood's worth (what everything else converted at), but never less
 * than the plain sum, so iron and crystal a player had stockpiled are not lost. An upgrade the bundle could pay
 * before (wood ≥ 30 × m) it can pay after (2 × wood ≥ 60 × m).
 */
export function goldFromLegacy(r: LegacyResources): number {
  const wood = count(r.wood), iron = count(r.iron), crystal = count(r.crystal);
  return Math.max(wood * LEGACY_WOOD_GOLD, wood + iron + crystal);
}

/** Whether a JSON value is a legacy bundle (has any of wood/iron/crystal) rather than a gold amount. */
export function isLegacyResources(v: unknown): v is LegacyResources {
  return typeof v === "object" && v !== null && ("wood" in v || "iron" in v || "crystal" in v);
}
